import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase, isSupabaseConfigured } from "./supabase.js";
import { useAuth } from "./auth.jsx";

const JournalContext = createContext(null);

const LS_SAVED = "smw:saved";
const LS_ENTRIES = "smw:entries";

function readLS(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}
function writeLS(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {}
}

export function JournalProvider({ children }) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(() => readLS(LS_SAVED, []));
  const [entries, setEntries] = useState(() => readLS(LS_ENTRIES, []));
  const useRemote = Boolean(isSupabaseConfigured && user && !user.guest);

  useEffect(() => {
    if (!useRemote) return;
    let cancelled = false;
    (async () => {
      const [s, e] = await Promise.all([
        supabase.from("saved_techniques").select("technique_id, created_at").eq("user_id", user.id),
        supabase.from("journal_entries").select("*").eq("user_id", user.id).order("created_at", { ascending: false }),
      ]);
      if (cancelled) return;
      if (s.data) setSaved(s.data.map((r) => r.technique_id));
      if (e.data) setEntries(e.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [useRemote, user]);

  const toggleSave = useCallback(
    async (techniqueId) => {
      const next = saved.includes(techniqueId)
        ? saved.filter((id) => id !== techniqueId)
        : [...saved, techniqueId];
      setSaved(next);
      writeLS(LS_SAVED, next);
      if (useRemote) {
        if (saved.includes(techniqueId)) {
          await supabase.from("saved_techniques").delete().match({ user_id: user.id, technique_id: techniqueId });
        } else {
          await supabase.from("saved_techniques").insert({ user_id: user.id, technique_id: techniqueId });
        }
      }
    },
    [saved, useRemote, user]
  );

  const addEntry = useCallback(
    async (entry) => {
      const record = {
        id: crypto.randomUUID(),
        technique_id: entry.techniqueId,
        note: entry.note,
        created_at: new Date().toISOString(),
      };
      const next = [record, ...entries];
      setEntries(next);
      writeLS(LS_ENTRIES, next);
      if (useRemote) {
        await supabase.from("journal_entries").insert({
          user_id: user.id,
          technique_id: entry.techniqueId,
          note: entry.note,
        });
      }
    },
    [entries, useRemote, user]
  );

  return (
    <JournalContext.Provider value={{ saved, entries, toggleSave, addEntry, useRemote }}>
      {children}
    </JournalContext.Provider>
  );
}

export function useJournal() {
  return useContext(JournalContext);
}
